import { GAME_CONFIG, type AuctionDurationHours, type MarketConfig } from './config';


export function saleFee(price: number, config: MarketConfig = GAME_CONFIG.market): number {
  if (!Number.isInteger(price) || price <= 0) return 0;
  return Math.ceil((price * config.saleFeePercent) / 100);
}

export function saleNet(price: number, config: MarketConfig = GAME_CONFIG.market): number {
  return Math.max(0, price - saleFee(price, config));
}

export interface SystemBuyback {
  
  price: number;
  
  fee: number;
  
  net: number;
}


export function systemBuyback(referenceValue: number, config: MarketConfig = GAME_CONFIG.market): SystemBuyback {
  const value = Math.max(0, Math.floor(referenceValue));
  const price = Math.floor((value * config.systemBuybackPercent) / 100);
  const fee = Math.floor((price * config.systemBuybackPotPercent) / 100);
  return { price, fee, net: price - fee };
}

export interface PriceBounds {
  min: number;
  max: number;
}


export function priceBounds(referenceValue: number, config: MarketConfig = GAME_CONFIG.market): PriceBounds {
  const value = Math.max(1, referenceValue);
  return {
    min: config.minPrice,
    max: Math.max(config.minPrice, value * config.maxPriceValueMultiplier),
  };
}

export function isPriceInBounds(price: number, referenceValue: number, config: MarketConfig = GAME_CONFIG.market): boolean {
  if (!Number.isInteger(price) || price <= 0) return false;
  const { min, max } = priceBounds(referenceValue, config);
  return price >= min && price <= max;
}


export function minNextBid(currentPrice: number, currentIncrement: number): number {
  return currentPrice + currentIncrement;
}


export function nextAuctionIncrement(
  currentIncrement: number,
  config: Pick<MarketConfig, 'auctionIncrementMultiplier'> = GAME_CONFIG.market,
): number {
  return Math.ceil(currentIncrement * config.auctionIncrementMultiplier);
}


export function maxNextBid(
  currentPrice: number,
  currentIncrement: number,
  config: Pick<MarketConfig, 'auctionMaxBidMultiplier'> = GAME_CONFIG.market,
): number {
  return Math.floor(minNextBid(currentPrice, currentIncrement) * config.auctionMaxBidMultiplier);
}


export function extendForAntiSnipe(
  endsAtIso: string,
  bidAtMs: number,
  antiSnipeMs: number = GAME_CONFIG.market.antiSnipeMs,
): string {
  const endsAtMs = Date.parse(endsAtIso);
  if (!Number.isFinite(endsAtMs)) return endsAtIso;
  if (endsAtMs - bidAtMs > antiSnipeMs) return endsAtIso;
  return new Date(bidAtMs + antiSnipeMs).toISOString();
}

export function isAuctionDurationHours(value: number): value is AuctionDurationHours {
  return (GAME_CONFIG.market.auctionDurationsHours as readonly number[]).includes(value);
}

export function auctionEndsAt(startsAtMs: number, durationHours: AuctionDurationHours): string {
  return new Date(startsAtMs + durationHours * 3_600_000).toISOString();
}

export interface TradeShapeInput {
  initiatorCharacterIds: readonly number[];
  recipientCharacterIds: readonly number[];
  animaFromInitiator: number;
  animaFromRecipient: number;
}

export type TradeShapeError =
  | 'empty_trade'
  | 'too_many_cards'
  | 'anima_both_sides'
  | 'anima_too_high'
  | 'duplicate_cards'
  | 'invalid_anima';

export function validateTradeShape(
  input: TradeShapeInput,
  config: Pick<MarketConfig, 'maxTradeAnima' | 'maxTradeCardsPerSide'> = GAME_CONFIG.market,
  recipientConfig: Pick<MarketConfig, 'maxTradeAnima' | 'maxTradeCardsPerSide'> = config,
): TradeShapeError | null {
  const { initiatorCharacterIds, recipientCharacterIds, animaFromInitiator, animaFromRecipient } = input;
  if (
    !Number.isInteger(animaFromInitiator) ||
    animaFromInitiator < 0 ||
    !Number.isInteger(animaFromRecipient) ||
    animaFromRecipient < 0
  ) {
    return 'invalid_anima';
  }
  if (animaFromInitiator > 0 && animaFromRecipient > 0) return 'anima_both_sides';
  if (animaFromInitiator > config.maxTradeAnima || animaFromRecipient > recipientConfig.maxTradeAnima) {
    return 'anima_too_high';
  }
  if (initiatorCharacterIds.length > config.maxTradeCardsPerSide || recipientCharacterIds.length > recipientConfig.maxTradeCardsPerSide) {
    return 'too_many_cards';
  }
  const all = [...initiatorCharacterIds, ...recipientCharacterIds];
  if (new Set(all).size !== all.length) return 'duplicate_cards';
  if (all.length === 0) return 'empty_trade';
  return null;
}

export function isSuspiciousLowPrice(
  price: number,
  referenceValue: number,
  config: MarketConfig = GAME_CONFIG.market,
): boolean {
  return price < referenceValue * config.lowPriceFlagRatio;
}

export function isSuspiciousHighPrice(
  price: number,
  referenceValue: number,
  config: MarketConfig = GAME_CONFIG.market,
): boolean {
  return price > referenceValue * config.highPriceFlagMultiplier;
}
